import React from 'react';
import { Modal, Box, Typography, Table, TableBody, TableRow, TableCell, Button } from '@mui/material';

const SSNVerificationModal = ({ open, onClose, verificationData, onManualVerification, loading }) => {
  const { 
    expectedData, 
    sparData, 
    reason = '', 
    status = '', 
    userId, 
    customerId,
    ssn, 
    isSsnValid 
  } = verificationData || {};

  // Ensure expectedData and sparData are always objects, even if null
  const expected = expectedData || {};
  const spar = sparData || {};
  const id = userId || customerId; // Support both userId and customerId

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="ssn-verification-modal-title"
      aria-describedby="ssn-verification-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <Typography 
          id="ssn-verification-modal-title" 
          variant="h5" 
          component="h2" 
          sx={{ 
            mb: 3, 
            color: status === 'failed' ? '#d32f2f' : '#2e7d32', 
            fontWeight: 600 
          }}
        >
          {status === 'failed' ? '⚠️ SSN Verification Failed' : '✓ SSN Verification Details'}
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: status === 'failed' ? '#ffebee' : '#e8f5e9', 
            borderRadius: 1, 
            color: status === 'failed' ? '#c62828' : '#2e7d32'
          }}
        >
          <strong>Status:</strong> {reason || 'Verification Successful'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Expected Data Column */}
          <Box sx={{ flex: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              User Data (Sent)
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>First Name:</TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {expected.firstName || 'N/A'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Last Name:</TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {expected.lastName || 'N/A'}
                  </TableCell>
                </TableRow>
                {expected.dateOfBirth && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Date of Birth:</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {expected.dateOfBirth}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {/* SPAR Data Column */}
          <Box sx={{ flex: 1, p: 2, borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              SPAR Data (Received)
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>First Name:</TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {spar.firstName || 'N/A'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Last Name:</TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {spar.lastName || 'N/A'}
                  </TableCell>
                </TableRow>
                {spar.dateOfBirth && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Date of Birth:</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {spar.dateOfBirth}
                    </TableCell>
                  </TableRow>
                )}
                {spar.idNummer && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>ID Number:</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {spar.idNummer}
                    </TableCell>
                  </TableRow>
                )}
                {spar.kon && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Gender:</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {spar.kon}
                    </TableCell>
                  </TableRow>
                )}
                {spar.country && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Country:</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {spar.country}
                    </TableCell>
                  </TableRow>
                )}
                {spar.address && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Address:</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {spar.address}
                    </TableCell>
                  </TableRow>
                )}
                {spar.postCity && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>City:</TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {spar.postCity}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {status === 'failed' && (
          <Typography variant="body2" sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, fontSize: '0.875rem' }}>
            <strong>Note:</strong> Red boxes indicate mismatched values. Green boxes indicate matching values.
          </Typography>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {isSsnValid === false && id && ssn && onManualVerification && (
            <Button
              variant="contained"
              onClick={() => onManualVerification(id, ssn)}
              disabled={loading}
              sx={{ backgroundColor: '#f57c00', '&:hover': { backgroundColor: '#ef6c00' } }}
            >
              Mark as verified
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ backgroundColor: '#1976d2' }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SSNVerificationModal;
